import React from 'react';
import PropTypes from 'prop-types';
import { WeezHeader, Button, Tab, SplitContainer, Message, Card, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import ProductModel from '../../../models/productModel';
import RenderFormProduct from '../../product/detail/settings/components/renderFormProduct';
import EventModel from '../../../models/eventModel';
import PutForSale from './putForSale';
import PutForSaleModel from '../../../models/productSaleModel';
import VariantModel from '../../../models/variantModel';
import { convertMenhirToPyvarPipeTranslate } from '../../../components/utils/pipeTranslateConvert';
import { useForm, requiredField } from '@weezevent/weezjs-core';

import { getProductsLanguages, getDefaultLanguage, removeDefaultLanguage } from './list/translationUtils';
import VariantsTable from '../../product/detail/variants/components/variantsTable';

export default function EditCrossSelling({ organization, productId, page = 0 }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    let [forSaleArray, setForSaleArray] = React.useState([]);
    let [salesToDelete, setSalesToDelete] = React.useState([]);
    const [isSaveButtonLoading, setIsSaveButtonLoading] = React.useState(false);
    const [variants, setVariants] = React.useState([]);

    // We’re not supposed to exceed the maximum number of characters. But we can if the description is done in the old BO
    // and in that case you can't modify the product in the NBO, this error is an indication to dodge run
    const validateDescription = React.useCallback((key, description) => {
        return description.length > 450 ? 'ticket.cross-selling.add.too_many_characters_description' : true;
    }, []);

    const [productForm, setProductForm, setProductFormObject, productFormErrors, productFormValidate] = useForm(
        {
            id: null,
            organization: organization.id,
            name: '',
            description: '',
            disabled: false,
            for_buyer: false,
            choose_none_option: false,
            display_quantity: false,
            display_out_of_stock: false,
            display_order: 0,
            category: 2,
            tva: null,
            translations: {
                name: {},
                description: {}
            }
        },
        {
            name: requiredField,
            tva: requiredField,
            description: validateDescription
        }
    );

    const productsLanguages = React.useMemo(() => getProductsLanguages(organization), [organization]);
    const defaultLanguage = React.useMemo(() => getDefaultLanguage(organization, productsLanguages), [organization, productsLanguages]);

    const [activeTab, setActiveTab] = React.useState(Number(page));
    const [submitting, setSubmitting] = React.useState(false);
    const [querySearch, setQuerySearch] = React.useState('');

    const getPipeTranslate = React.useCallback(
        string => {
            let translations = {};

            if (string.includes('|')) {
                string.split('|').forEach(item => {
                    let pipe = convertMenhirToPyvarPipeTranslate(item);
                    let regLang = pipe.slice(1, 6).toLowerCase();
                    pipe = pipe.slice(7, pipe.length);
                    if (productsLanguages?.find(l => regLang === l)) translations[regLang] = pipe;
                });
            }
            return translations;
        },
        [productsLanguages]
    );

    const initTranslations = React.useCallback(
        (obj, lang, fields) => {
            if (!obj.translations) obj.translations = {};
            fields?.forEach(field => {
                const pipeTranslate = getPipeTranslate(obj[field]);
                obj.translations[field] = { ...pipeTranslate, ...obj.translations[field] };
                //use name if no translations for default language
                if (obj.translations[field][lang]) obj[field] = obj.translations[field][lang];
                if (!obj.translations[field][lang]) obj.translations[field][lang] = obj[field];
                //we filter the languages keys that arent in organization
                Object.keys(obj.translations[field]).forEach(langKey => {
                    if (!productsLanguages.some(ol => ol === langKey)) obj.translations[field][langKey] = '';
                });
            });
            return obj;
        },
        [productsLanguages]
    );

    const [product, loadingProduct, sync] = ProductModel.useApiModel(
        {
            organizationId: organization.id,
            id: productId,
            launch: Boolean(productId),
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                let obj = response.object;
                //we init the translations in case we don't have any
                obj = initTranslations(obj, defaultLanguage, ['name', 'description']);
                setProductFormObject(
                    {
                        id: obj.id,
                        organization: organization.id,
                        name: obj.name,
                        description: obj.description,
                        disabled: obj.disabled,
                        for_buyer: obj.for_buyer,
                        choose_none_option: obj.choose_none_option,
                        display_quantity: obj.display_quantity,
                        display_out_of_stock: obj.display_out_of_stock,
                        display_order: obj.display_order,
                        category: obj.category,
                        tva: obj.tva,
                        translations: obj.translations
                    },
                    true
                );
            }
        },
        [productId, organization, defaultLanguage]
    );

    const [, loaderVariant, syncVariants] = VariantModel.useApiModel(
        {
            organizationId: organization.id,
            productId: productId,
            launch: Boolean(productId),
            query: {
                include: ['transaction_total_count']
            },
            onSuccess: response => {
                let objs = response;
                //we init the translations in case we don't have any
                objs = objs.map(obj => initTranslations(obj, defaultLanguage, ['name']));
                setVariants(objs);
            }
        },
        [productId, organization, defaultLanguage]
    );

    const [events, loadingEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            query: {
                search: querySearch,
                limit: 50,
                offset: 0,
                publication_status__in: ['draft', 'published'],
                time_status: ['upcoming', 'ongoing']
            }
        },
        [organization, querySearch]
    );

    const panes = React.useMemo(() => {
        const existingProduct = Boolean(productId);
        return [
            {
                menuItem: t('ticket.cross-selling.add.pan.article'),
                menuIcon: 'package'
            },
            {
                menuItem: t('ticket.cross-selling.add.pan.variant'),
                menuIcon: 'cart',
                disabled: !existingProduct
            },
            {
                menuItem: t('ticket.cross-selling.add.pan.toSell'),
                menuIcon: 'user',
                disabled: !existingProduct || variants?.length === 0
            }
        ];
    }, [productId, variants]);

    const setTranslate = React.useCallback(string => {
        let reg = new RegExp(/\[([a-z]{2}[-|_]?[A-Z]{0,2})\](.*)/);

        let translations = { 'fr-FR': string, 'en-GB': '', 'it-IT': '', 'es-ES': '', 'en-US': '' };

        if (string.includes('|')) {
            string.split('|').forEach(item => {
                let matches = item.match(reg);
                if (matches?.length > 2) {
                    let locale = matches[1].replace('_', '-');
                    translations[locale] = matches[2];
                }
            });
        }
        return translations;
    }, []);

    const onEdit = React.useCallback(
        value => {
            if (!productFormValidate()) {
                return toast.error(t('ticket.cross-selling.add.form_error'));
            }
            setSubmitting(true);
            return new ProductModel({ id: value.id, ...value })
                .save({
                    organizationId: organization.id
                })
                .then(() => {
                    toast.success(t('ticket.cross-selling.add.productEdited'));
                })
                .catch(e => {
                    toast.error(t(`ticket.cross-selling.add.productEditError`));
                })
                .finally(() => {
                    setSubmitting(false);
                    sync();
                });
        },
        [organization, sync, defaultLanguage]
    );

    const postMEV = React.useCallback(() => {
        setIsSaveButtonLoading(true);
        let arrayPromiseSaleDelete = [];
        if (salesToDelete?.length) {
            arrayPromiseSaleDelete = salesToDelete.map(obj => {
                return new PutForSaleModel(obj).delete({
                    organizationId: organization.id,
                    productId: productId
                });
            });
        }

        let _new = [...forSaleArray];
        let arrayPromiseSaleCreate = forSaleArray.map(r => {
            return new PutForSaleModel(r).save({
                organizationId: organization.id,
                productId: productId
            });
        });
        Promise.all([...arrayPromiseSaleDelete, ...arrayPromiseSaleCreate]).then(arrayResponse => {
            for (let result of arrayResponse) {
                if (result.status === 201) {
                    let f = forSaleArray.find(l => result.response.rate === l.rate);
                    if (!f.id) {
                        let i = forSaleArray.findIndex(l => result.response.rate === l.rate);

                        _new[i].id = result.response.id;
                    }
                }
            }
            setSalesToDelete([]);
            setForSaleArray(_new);
            toast.success(t('ticket.cross-selling.putForSale.edited'));
            setIsSaveButtonLoading(false);
        });
    }, [salesToDelete, forSaleArray, organization, productId]);

    return (
        <>
            <WeezHeader
                title={productId ? t('ticket.cross-selling.add.editTitle') : t('ticket.cross-selling.add.title')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.id}/cross-selling`) }}
                rightComponent={[
                    <Button
                        primary
                        key="rate"
                        isLoading={isSaveButtonLoading}
                        onClick={() => {
                            if (activeTab === 2) {
                                postMEV();
                                return;
                            }
                            if (productForm.id || productId) {
                                const payload = {
                                    ...productForm,
                                    translations: removeDefaultLanguage(productForm.translations, defaultLanguage)
                                };
                                onEdit(payload, productForm.id || productId);
                            }
                        }}
                        label={t('ticket.common.action.save')}
                    />
                ]}
            />

            <SplitContainer
                additional={
                    <div>
                        <Message mini title={t('ticket.common.tips')}>
                            {t(`ticket.cross-selling.info${activeTab + 1}`)}
                        </Message>
                    </div>
                }
            >
                <Tab
                    panes={panes}
                    onTabSelected={(tab, idx) => {
                        setActiveTab(idx);
                    }}
                    activeTab={activeTab}
                >
                    {activeTab === 0 && (
                        <Card>
                            <Card.Header>
                                <Card.Title title={t('ticket.cross-selling.add.information')} />
                                <Card.SubTitle title={t('ticket.cross-selling.add.addProductSub')} />
                            </Card.Header>
                            <RenderFormProduct
                                form={productForm}
                                setForm={setProductForm}
                                setFormObject={setProductFormObject}
                                formErrors={productFormErrors}
                                formValidate={productFormValidate}
                                variants={variants}
                                transaction_total_count={product?.transaction_total_count}
                                defaultLanguage={defaultLanguage}
                                isLoading={Boolean(productId && loadingProduct) || submitting || loaderVariant}
                            />
                        </Card>
                    )}
                    {activeTab === 1 && (
                        <div>
                            <VariantsTable
                                organization={organization}
                                productId={productId || productForm.id}
                                loading={loaderVariant}
                                variants={variants}
                                syncVariants={syncVariants}
                                setVariants={setVariants}
                                productLanguages={productsLanguages}
                                oldProductSync={sync}
                            />
                        </div>
                    )}
                    {activeTab === 2 && (
                        <>
                            <PutForSale
                                events={events}
                                querySearch={querySearch}
                                setQuerySearch={setQuerySearch}
                                loadingEvents={loadingEvents}
                                setForSaleArray={setForSaleArray}
                                forSaleArray={forSaleArray}
                                salesToDelete={salesToDelete}
                                onSalesToDelete={setSalesToDelete}
                                category={productForm.category}
                                organization={organization}
                                productId={parseInt(productId, 10) || parseInt(productForm.id, 10)}
                                setTranslate={setTranslate}
                            />
                        </>
                    )}
                </Tab>
            </SplitContainer>
        </>
    );
}

EditCrossSelling.propTypes = {
    organization: PropTypes.object.isRequired
};
